import React, { useEffect, useMemo, useState, useRef } from "react";
import classes from "@css/components/website/Header.module.css";
import dimensionLogo from "../../assets/landing_page/dimension-logo.svg";
import dimensionLogoDark from "../../assets/landing_page/dimension-logo-dark.svg";
import MenuIcon from "../../assets/menu-icon.svg";
import MenuIconDark from "../../assets/menu-icon-dark.svg";
import MobileMenu from "./MobileMenu";
import LoginModal from "../LoginModal";
import { Drawer, IconButton, useMediaQuery } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { signOut } from "../../store/actions/auth";
import { fetchMyBookings } from "../../store/actions/booking";
import { BooleanParam, useQueryParam } from "use-query-params";
import useScrollPosition from "../../shared/hooks/useScrollPosition";
import tempDataConstants from "../../store/constants/tempDataTypes";
import { roles } from "../../constants/roles";
import history from "../../history";

// Import the new components
import CustomerLoggedOut from "./Customer/LoggedOut";
import CustomerLoggedIn from "./Customer/LoggedIn";
import ProviderLoggedIn from "./Provider/LoggedIn";

function Header({ opaque }: { opaque?: boolean }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width:768px)");
  const posts = useSelector((state: any) => state.wordpress.posts);

  const [open, setOpen] = useState<boolean>(false);
  const [formId, setFormId] = useState<any>(null);
  const [showNumber, setShowNumber] = useState(false);
  const [launchLogin] = useQueryParam("login", BooleanParam);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isOpaque, setIsOpaque] = useState<boolean>(opaque ?? false);

  const pathname = history.location.pathname.split("/")?.[1]?.toLowerCase();
  const isDisposalPage = [
    "beverage-disposal",
    "fire-debris-removal",
    "hazardous-waste-disposal",
    "solar-panel-disposal",
    "commercial-solar-panel-uninstallation-and-disposal",
    "hand-sanitizer-disposal",
    "sanitizer-disposal",
    "alcohol-disposal",
    "junk-disposal",
    "compost-disposal",
  ].includes(pathname);

  const subscribed = useRef(false);

  // Selectors
  const isLoggedIn = useSelector((state: any) => state.firebase.auth?.uid);
  const profile = useSelector((state: any) => state.firebase.profile);
  const warriorBookings = useSelector((state: any) => state?.booking?.myBookings);

  // Derived state
  const isProvider = profile?.userType === roles.provider.value;
  const isApproved = !profile?.approvalState || profile?.approvalState === "complete";
  const isBanned = profile.isBanned;
  const isAbbreviated = !!(profile?.isAbbreviated || (isProvider && profile?.isSignupCompleted && profile?.businessId));

  const scrollPosition = useScrollPosition();

  const query = useMemo(
    () => ({
      key: profile?.uid,
      custom: {
        market: profile?.smsSettings?.address?.city,
        email: profile?.email ?? "",
        userType: profile?.userType ?? "",
      },
    }),
    [profile]
  );

  // Effects
  useEffect(() => {
    if (isAbbreviated) dispatch(fetchMyBookings(profile?.uid));
  }, [isAbbreviated]);

  useEffect(() => {
    subscribed.current = true;
    return () => {
      subscribed.current = false;
    };
  }, []);

  useEffect(() => {
    const offset = isMobile ? 30 : 200;
    setIsOpaque(opaque ? true : Math.round(scrollPosition / offset) > 0);
  }, [scrollPosition, opaque]);

  useEffect(() => {
    if (
      [
        "commercial-solar-panel-uninstallation-and-disposal",
        "hand-sanitizer-disposal",
        "sanitizer-disposal",
        "solar-panel-disposal",
        "solar-panel-sell",
        "hazardous-waste-disposal",
        "fire-debris-removal",
        "beverage-disposal",
        "alcohol-disposal",
        "compost-disposal",
      ].includes(pathname)
    ) {
      setShowNumber(true);
      setFormId(
        pathname === "hand-sanitizer-disposal"
          ? "sanitizer-disposal"
          : pathname === "commercial-solar-panel-uninstallation-and-disposal"
          ? "solar-panel-disposal"
          : pathname
      );
    }
  }, [pathname]);

  // Handlers
  const handleLogin = () => {
    dispatch({
      type: tempDataConstants.SET_TEMP_USER,
      userType: roles.customer.value,
    });
    setOpen(true);
  };

  const handleSignOut = () => dispatch(signOut());

  const toggleDrawer = (open: boolean) => (event: any) => {
    if (event?.type === "keydown" && (event?.key === "Tab" || event?.key === "Shift")) {
      return;
    }
    setIsDrawerOpen(open);
  };

  useEffect(() => {
    if (launchLogin) {
      handleLogin();
    }
  }, [launchLogin]);

  // Styles
  const headerClasses = {
    container: isOpaque ? classes.containerOpaque : classes.container,
  };
  const menuIconProps: any = { style: { color: isOpaque ? "black" : "white" } };

  // Render appropriate header content based on user state
  const renderHeaderContent = () => {
    if (!isLoggedIn) {
      return (
        <CustomerLoggedOut
          isOpaque={isOpaque}
          showNumber={showNumber}
          formId={formId}
          isDisposalPage={isDisposalPage}
          pathname={pathname}
          posts={posts}
          handleLogin={handleLogin}
        />
      );
    }

    if (isProvider) {
      return (
        <ProviderLoggedIn
          isOpaque={isOpaque}
          profile={profile}
          isApproved={isApproved}
          isBanned={isBanned}
          isAbbreviated={isAbbreviated}
          warriorBookings={warriorBookings}
          signOut={handleSignOut}
        />
      );
    }

    return <CustomerLoggedIn isOpaque={isOpaque} isDisposalPage={isDisposalPage} pathname={pathname} posts={posts} signOut={handleSignOut} />;
  };

  return (
    <div className={headerClasses.container}>
      <div className={classes.contents}>
        <img
          className={classes.headerLogo}
          onClick={() => history.push("/")}
          style={{ cursor: "pointer" }}
          src={isOpaque ? dimensionLogoDark : dimensionLogo}
          alt="Dimension Logo"
        />

        <div className={classes.headerButtons}>{renderHeaderContent()}</div>

        {isMobile && (
          <IconButton disableRipple className={classes.drawerButton} onClick={toggleDrawer(true)}>
            <img src={isOpaque ? MenuIconDark : MenuIcon} {...menuIconProps} alt="Menu button" />
          </IconButton>
        )}
      </div>

      <Drawer
        className={classes.drawer}
        classes={{ paperAnchorRight: classes.drawerMenu }}
        anchor="right"
        open={isDrawerOpen}
        onClose={toggleDrawer(false)}
      >
        <MobileMenu isOpaque={isOpaque} onClose={toggleDrawer(false)} handleLogin={handleLogin} warriorBookings={warriorBookings} />
      </Drawer>

      <LoginModal open={open} setOpen={setOpen} />
    </div>
  );
}

export default Header;
